import React from "react"
import apolloLogger from "apollo-link-logger"
import { ApolloClient, ApolloProvider, InMemoryCache, ApolloLink } from "@apollo/client"
import { createUploadLink } from "apollo-upload-client"
import { BatchHttpLink } from "@apollo/client/link/batch-http"
import { setContext } from "@apollo/link-context"
import type { Auth0ContextInterface } from "@auth0/auth0-react"
import { useAuth0 } from "@auth0/auth0-react"
import { loadEnvVars } from "../utils/loadEnvVars"

type GetAccessTokenSilentlyType = Pick<Auth0ContextInterface, "getAccessTokenSilently">["getAccessTokenSilently"]
type AuthorizedApolloProviderProps = {
  client?: (getAccessTokenSilently: GetAccessTokenSilentlyType) => ApolloClient<unknown>
  children?: React.ReactNode | React.ReactNode[]
}

const asyncAuthLink = (getAccessTokenSilently: GetAccessTokenSilentlyType) =>
  setContext(async operation => {
    const token = await getAccessTokenSilently()

    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "apollo-require-preflight": "true",
        "x-apollo-operation-name": operation.operationName || "UnknownOp"
      }
    }
  })

const hasActualFile = (v: unknown): boolean => {
  if (!v) return false
  if (typeof File !== "undefined" && v instanceof File) return true
  if (typeof Blob !== "undefined" && v instanceof Blob) return true
  if (Array.isArray(v)) return v.some(hasActualFile)
  if (typeof v === "object") return Object.values(v as Record<string, unknown>).some(hasActualFile)
  return false
}

const defaultApolloClient = (getAccessTokenSilently: GetAccessTokenSilentlyType) => {
  const { graphqlApiGateway } = loadEnvVars()
  const uri = `${graphqlApiGateway}/graphql`
  const httpLink = ApolloLink.split(
    operation => hasActualFile(operation.variables),
    createUploadLink({
      uri,
      headers: {
        "keep-alive": "true"
      },
      credentials: "include"
    }) as ApolloLink,
    new BatchHttpLink({ uri, credentials: "include", batchMax: 10, batchInterval: 20 })
  )

  return new ApolloClient({
    link: ApolloLink.from([asyncAuthLink(getAccessTokenSilently), apolloLogger, httpLink]),
    cache: new InMemoryCache()
  })
}

const AuthorizedApolloProvider: React.FunctionComponent<AuthorizedApolloProviderProps> = ({
  children,
  client = defaultApolloClient
}) => {
  const { getAccessTokenSilently } = useAuth0()
  const authorizedApolloClient = client(getAccessTokenSilently)

  return <ApolloProvider client={authorizedApolloClient}>{children}</ApolloProvider>
}

export default AuthorizedApolloProvider
